import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import StripeConnectSection from '../../../components/StripeConnect/StripeConnectSection'
import { ROUTES } from '../../../constants/routes'
import useApi from '../../../hooks/useApi'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import {
  CHARITY_VERIFICATION_STATUS_VARIANTS,
  getCharityStripeRejectionReason,
  getCharityVerificationBadgeKey,
  isCharityStripeSuspended,
  isCharityStripeVerified,
} from '../../../lib/stripeConnect'
import CharityOnboardingProgress from '../../../components/Profiles/CharityProfile/CharityOnboardingProgress'
import CharityVerificationRequirementsModal from './CharityVerificationRequirementsModal'

const TRANSLATION_PREFIX = 'charities.dashboard.stripe'

export default function CharityStripeConnectSection({
  charityId,
  returnPath,
  onStatusChange,
  onVerificationComplete,
}) {
  const { t } = useTranslation()
  const { fetchUser } = useCurrentUser()
  const [showRequirementsModal, setShowRequirementsModal] = useState(false)
  const [continuing, setContinuing] = useState(false)
  const pendingConnectRef = useRef(null)

  const {
    createCharityStripeAccount,
    getCharityStripeAccountStatus,
    getCharityStripeOnboardingLink,
  } = useApi()

  const stripeReturnPath = returnPath ?? `${ROUTES.CHARITY_DASHBOARD}`
  const returnUrl = `${window.location.origin}${stripeReturnPath}?stripe_return=true&charity_id=${charityId}`
  const refreshUrl = `${window.location.origin}${stripeReturnPath}?stripe_refresh=true&charity_id=${charityId}`

  const handleConnectClick = useCallback(connect => {
    pendingConnectRef.current = connect
    setShowRequirementsModal(true)
  }, [])

  const handleRequirementsContinue = useCallback(async () => {
    setShowRequirementsModal(false)
    setContinuing(true)
    try {
      await pendingConnectRef.current?.()
    } finally {
      setContinuing(false)
    }
  }, [])

  const handleVerificationComplete = useCallback(
    status => {
      fetchUser()
      onVerificationComplete?.(status)
    },
    [fetchUser, onVerificationComplete],
  )

  const getActionLabelKey = useCallback(stripeStatus => {
    if (getCharityStripeRejectionReason(stripeStatus)) return 'resubmitButton'
    return null
  }, [])

  const renderExtra = useCallback(
    ({ stripeStatus }) => (
      <>
        <CharityOnboardingProgress stripeStatus={stripeStatus} />
        {isCharityStripeSuspended(stripeStatus) ? (
          <Alert variant="warning" className="mt-3 mb-0">
            {t(`${TRANSLATION_PREFIX}.suspendedMessage`)}
          </Alert>
        ) : null}
        {getCharityStripeRejectionReason(stripeStatus) ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {t(`${TRANSLATION_PREFIX}.rejectionMessage`, {
              reason: getCharityStripeRejectionReason(stripeStatus),
            })}
          </Alert>
        ) : null}
      </>
    ),
    [t],
  )

  if (!charityId) {
    return null
  }

  return (
    <>
      <StripeConnectSection
        createStripeAccount={createCharityStripeAccount}
        entityId={charityId}
        getAccountStatus={getCharityStripeAccountStatus}
        getActionLabelKey={getActionLabelKey}
        getBadgeStatusKey={getCharityVerificationBadgeKey}
        getOnboardingLink={getCharityStripeOnboardingLink}
        isComplete={isCharityStripeVerified}
        onConnectClick={handleConnectClick}
        onStatusChange={onStatusChange}
        onVerificationComplete={handleVerificationComplete}
        refreshUrl={refreshUrl}
        renderExtra={renderExtra}
        returnUrl={returnUrl}
        shouldHideAction={isCharityStripeSuspended}
        statusVariants={CHARITY_VERIFICATION_STATUS_VARIANTS}
        translationPrefix={TRANSLATION_PREFIX}
        bare
      />
      <CharityVerificationRequirementsModal
        show={showRequirementsModal}
        onHide={() => setShowRequirementsModal(false)}
        onContinue={handleRequirementsContinue}
        continuing={continuing}
      />
    </>
  )
}

CharityStripeConnectSection.propTypes = {
  charityId: PropTypes.number.isRequired,
  onStatusChange: PropTypes.func,
  onVerificationComplete: PropTypes.func,
  returnPath: PropTypes.string,
}
